import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { EMPTY, first, firstValueFrom, Observable, of, ReplaySubject, Subject, switchMap, timer } from 'rxjs';
import { BlockingLoaderDialogComponent } from './blocking-loader-dialog.component';
import { BlockingLoaderProgress } from './BlockingLoaderProgress';

const MIN_MS_TO_SHOW_MODAL = 300;
const MS_TO_HIDE_VIEW = 100;

@Injectable()
export class BlockingLoaderService {
    private _startShowing: number;
    private _ref: MatDialogRef<BlockingLoaderDialogComponent> | null;
    private _visible = false;
    private _visible$ = new Subject<boolean>();
    private _progress: BlockingLoaderProgress;
    private _progress$ = new Subject<BlockingLoaderProgress>();

    get visible$(): Observable<boolean> {
        return this._visible$.asObservable();
    }
    get progress$(): Observable<BlockingLoaderProgress> {
        return this._progress$.asObservable();
    }
    constructor(private _materialDialogService: MatDialog) {
        this.updateState(false);
    }

    reportProgress(progress: BlockingLoaderProgress): void {
        if (this._progress?.inProgress == progress.inProgress &&
            this._progress?.percentage == progress.percentage) {
            return;
        }
        this._progress = progress;
        this._progress$.next(this._progress);
    }

    showAsync(title?: string): Promise<void> {
        return firstValueFrom(this.show(title));
    }

    show(title?: string): Observable<void> {
        if (this._visible) {
            if (title) {
                this._ref.componentInstance.title = title;
            }
            return of(null);
        }
        this.updateState(true);
        this._startShowing = Date.now();
        this._ref = this._materialDialogService
            .open(BlockingLoaderDialogComponent, {
                disableClose: true,
                panelClass: 'srp-blocking-loader-dialog-panel',
                data: {
                    title,
                    showBlockingProgress$: this.visible$,
                    fileProgress$: this.progress$,
                },
            });
        return this._ref.afterOpened();
    }

    hideAsync(): Promise<void> {
        return firstValueFrom(this.hide());
    }

    hide(): Observable<void> {
        if (!this._visible) {
            return of(null);
        }
        const afterHidden$ = new ReplaySubject<void>(1);
        timer(0)
            .pipe(
                switchMap(() => {
                    const delta = Date.now() - this._startShowing;
                    if (delta < MIN_MS_TO_SHOW_MODAL) {
                        const timeToShow = MIN_MS_TO_SHOW_MODAL - delta;
                        return timer(timeToShow).pipe(
                            switchMap(() => {
                                this.updateState(false);
                                return timer(MS_TO_HIDE_VIEW);
                            }));
                    } else {
                        this.updateState(false);
                        return timer(MS_TO_HIDE_VIEW);
                    }
                }),
                first(),
            )
            .subscribe(() => {
                afterHidden$.next(null);
                afterHidden$.complete();
            });
        return afterHidden$;
    }

    hideAndReturnEmpty(): Observable<never> {
        return this
            .hide()
            .pipe(switchMap(() => EMPTY));
    }

    private updateState(show: boolean): void {
        if (this._visible === show) {
            return;
        }
        this._visible = show;
        this._visible$.next(this._visible);
        this._progress = null;
        this._progress$.next(null);
    }
}